<script setup>
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import EditableNumberCell from '@js/components/tables/cells/EditableNumberCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'position',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Position',
                    width: 0,
                }),
            cell: (props) =>
                h(EditableNumberCell, {
                    number: props.row.original.position,
                    url: route(
                        'front.projectManagement.postDocumentGroupPosition',
                        {
                            documentGroup: props.row.original.id,
                        },
                    ),
                    fieldName: 'position',
                    label: 'Position du groupe',
                    min: 0,
                    integer: true,
                    callback: () => updateData(),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'documentsCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Documents',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.documentsCount,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.documentGroup',
                                    {
                                        documentGroup: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.postDocumentGroups'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="route('front.projectManagement.documentGroup')"
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
